import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FloppyDisk, UploadSimple, Image as ImageIcon } from "@phosphor-icons/react";

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default function DaikinPoSettingsDialog({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [s, setS] = useState({});
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/daikin-pos/settings"); setS(data || {}); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const up = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Logo must be under 1.5 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => up("daikin_logo_url", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/daikin-pos/settings", {
        dealer_code: s.dealer_code, seller_name: s.seller_name, ordering_location: s.ordering_location,
        dealer_name: s.dealer_name, dealer_address: s.dealer_address, dealer_gstin: s.dealer_gstin,
        dealer_sign_name: s.dealer_sign_name, dealer_sign_title: s.dealer_sign_title,
        daikin_logo_url: s.daikin_logo_url,
        default_payment_terms: s.default_payment_terms, default_gst_pct: Number(s.default_gst_pct) || 0,
        default_cash_discount_pct: Number(s.default_cash_discount_pct) || 0, default_freight: s.default_freight,
      });
      toast.success("Daikin OFN settings saved");
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="daikin-settings-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px] flex items-center gap-2">
            <span className="text-[#003a8c] font-extrabold">DAIKIN</span> OFN Settings
          </DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500">
            Dealer identity, defaults and the Daikin logo that appears on every OFN. Used across all new Daikin POs.
          </DialogDescription>
        </DialogHeader>

        {loading ? (<div className="py-8 text-center text-slate-400 text-sm">Loading…</div>) : (
          <div className="space-y-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-[#003a8c] mb-2">Daikin Logo</div>
              <div className="flex items-center gap-4">
                <div className="h-16 w-36 rounded-md border border-dashed border-slate-300 flex items-center justify-center bg-slate-50 overflow-hidden">
                  {s.daikin_logo_url ? (<img src={s.daikin_logo_url} alt="Daikin logo" className="max-h-14 max-w-32 object-contain" data-testid="daikin-logo-preview" />) : (<span className="text-[10px] text-slate-400 flex items-center gap-1"><ImageIcon size={14} /> No logo</span>)}
                </div>
                <div className="space-y-1.5">
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} className="hidden" data-testid="daikin-logo-input" />
                  <Button variant="outline" size="sm" className="h-8 text-[12px]" onClick={() => fileRef.current?.click()} data-testid="daikin-logo-upload-btn"><UploadSimple size={14} className="mr-1.5" /> Upload official Daikin logo</Button>
                  {s.daikin_logo_url && <button className="block text-[11px] text-red-500" onClick={() => up("daikin_logo_url", "")} data-testid="daikin-logo-remove">Remove</button>}
                  <p className="text-[10px] text-slate-400">PNG/JPG/WebP, under 1.5 MB. Until uploaded, a “DAIKIN” text header is used.</p>
                </div>
              </div>
            </div>

            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-[#003a8c] mb-2">Dealer Identity</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="Dealer Code"><Input value={s.dealer_code || ""} onChange={(e) => up("dealer_code", e.target.value)} className="h-8 text-[12px]" data-testid="daikin-set-dealer-code" /></Field>
                <Field label="Dealer Name"><Input value={s.dealer_name || ""} onChange={(e) => up("dealer_name", e.target.value)} className="h-8 text-[12px]" data-testid="daikin-set-dealer-name" /></Field>
                <Field label="Dealer GSTIN"><Input value={s.dealer_gstin || ""} onChange={(e) => up("dealer_gstin", e.target.value)} className="h-8 text-[12px] font-mono" data-testid="daikin-set-gstin" /></Field>
                <Field label="Dealer Billing Address" className="col-span-2 md:col-span-3"><Textarea rows={2} value={s.dealer_address || ""} onChange={(e) => up("dealer_address", e.target.value)} className="text-[12px]" data-testid="daikin-set-address" /></Field>
                <Field label="Daikin Entity (seller)"><Input value={s.seller_name || ""} onChange={(e) => up("seller_name", e.target.value)} className="h-8 text-[12px]" data-testid="daikin-set-seller" /></Field>
                <Field label="Ordering Location"><Input value={s.ordering_location || ""} onChange={(e) => up("ordering_location", e.target.value)} className="h-8 text-[12px]" data-testid="daikin-set-location" /></Field>
                <Field label="Signature line (For …)"><Input value={s.dealer_sign_name || ""} onChange={(e) => up("dealer_sign_name", e.target.value)} className="h-8 text-[12px]" data-testid="daikin-set-sign" /></Field>
              </div>
            </div>

            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-[#003a8c] mb-2">Defaults</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Payment Terms"><Input value={s.default_payment_terms || ""} onChange={(e) => up("default_payment_terms", e.target.value)} className="h-8 text-[12px]" data-testid="daikin-set-payment" /></Field>
                <Field label="Freight"><Input value={s.default_freight || ""} onChange={(e) => up("default_freight", e.target.value)} className="h-8 text-[12px]" data-testid="daikin-set-freight" /></Field>
                <Field label="GST %"><Input type="number" value={s.default_gst_pct ?? 18} onChange={(e) => up("default_gst_pct", e.target.value)} className="h-8 text-[12px]" data-testid="daikin-set-gst" /></Field>
                <Field label="Cash Discount %"><Input type="number" value={s.default_cash_discount_pct ?? 0} onChange={(e) => up("default_cash_discount_pct", e.target.value)} className="h-8 text-[12px]" data-testid="daikin-set-cd" /></Field>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} className="h-9 text-[12px]">Cancel</Button>
          <Button onClick={save} disabled={busy || loading} className="h-9 text-[12px] bg-[#003a8c] hover:bg-[#002a66] text-white" data-testid="daikin-settings-save"><FloppyDisk size={14} className="mr-1.5" /> {busy ? "Saving…" : "Save settings"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
